import React from 'react';
import { Command } from 'cmdk';
import type { Command as CommandType } from '../types';
import { formatShortcut } from '../utils/keyboardShortcuts';
import styles from '../CommandPalette.module.scss';
import { KeywordChips } from './KeywordChip';

const renderIcon = (icon: React.ComponentType<any> | React.ReactNode): React.ReactNode => {
  if (typeof icon === 'function') {
    const Icon = icon as React.ComponentType<any>;
    return <Icon />;
  }
  return icon as React.ReactNode;
};

interface CommandItemProps {
  command: CommandType;
  onSelect: (command: CommandType, isSecondary?: boolean) => void;
  showShortcuts?: boolean;
  /** When provided, shows the numeric score as a small debug badge. */
  score?: number;
}

export const CommandItem: React.FC<CommandItemProps> = ({
  command,
  onSelect,
  showShortcuts = true,
  score,
}) => {
  const handleSelect = () => {
    onSelect(command, false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && command.secondary) {
      e.preventDefault();
      e.stopPropagation();
      onSelect(command, true);
    }
  };

  const isDisabled = command.enabled === false;

  return (
    <Command.Item
      className={styles.itemContainer}
      value={command.id}
      onSelect={handleSelect}
      onKeyDown={handleKeyDown}
      data-disabled={isDisabled}
      disabled={isDisabled}
    >
      {command.render
        ? <command.render />
        : (
          <>
            {command.icon && (
              <div className={styles.iconContainer}>
                {renderIcon(command.icon)}
              </div>
            )}

            <div className={styles.itemContent}>
              
              <div className={styles.itemName}>
                <span className={styles.itemNameText}>{command.label}</span>
              </div>
              {command.description && (
                <div className={styles.itemDescription}>{command.description}</div>
              )}
              <div className="flex flex-row items-center gap-1">
                <KeywordChips keywords={command.keywords} />
              </div>
            </div>

            {score !== undefined && (
              <span className={styles.debugScoreBadge}>{score.toFixed(1)}</span>
            )}

            {showShortcuts && (
              <div className={styles.shortcutContainer}>
                {command.secondary && (
                  <div className={styles.secondaryAction}>
                    {command.secondary.icon && renderIcon(command.secondary.icon)}
                    <span>{command.secondary.label}</span>
                    {command.secondary.shortcut && (
                      <kbd className={styles.shortcutKey}>{formatShortcut(`ctrl+enter`)}</kbd>
                    )}
                  </div>
                )}

                {command.shortcut && (
                  <kbd className={styles.shortcutKey}>{formatShortcut(command.shortcut)}</kbd>
                )}
              </div>
            )}
          </>
        )
      }
    </Command.Item>
  );
};